import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { beehiveTheme } from '../../styles/beehiveTheme';

const HeroContainer = styled(Box)(({ theme }) => ({
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  background: '#0a0a0a',
  position: 'relative',
  overflow: 'hidden',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

// SVG Honeycomb pattern
const HoneycombSVG = styled('svg')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
  opacity: 0.3,
});

const HexagonOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
  zIndex: 1,
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  color: beehiveTheme.neutral.white,
}));

const Headline = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '3.5rem',
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  color: beehiveTheme.neutral.white,
  textShadow: '0 2px 20px rgba(0,0,0,0.8)',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const Subheadline = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  lineHeight: 1.6,
  marginBottom: theme.spacing(4),
  color: beehiveTheme.accent.light,
  textShadow: '0 2px 15px rgba(0,0,0,0.8)',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: beehiveTheme.accent.main,
  color: beehiveTheme.neutral.white,
  fontWeight: 600,
  fontSize: '1.1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: beehiveTheme.shadows.lg,
  '&:hover': {
    backgroundColor: beehiveTheme.accent.dark,
    transform: 'translateY(-2px)',
    boxShadow: beehiveTheme.shadows.xl,
  },
  transition: 'all 0.3s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  color: beehiveTheme.neutral.white,
  fontWeight: 600,
  fontSize: '1.1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: '8px',
  textTransform: 'none',
  border: `2px solid ${beehiveTheme.neutral.white}`,
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    transform: 'translateY(-2px)',
    borderColor: beehiveTheme.accent.main,
  },
  transition: 'all 0.3s ease',
}));

export default function HeroSection() {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  // Generate hexagon path
  const createHexagon = (cx, cy, size) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      const x = cx + size * Math.cos(angle);
      const y = cy + size * Math.sin(angle);
      points.push(`${x},${y}`);
    }
    return points.join(' ');
  };

  // Generate honeycomb grid
  const generateHoneycomb = () => {
    const hexagons = [];
    const hexSize = 40;
    const hexWidth = Math.sqrt(3) * hexSize; // Width between centers horizontally
    const hexHeight = hexSize * 2; // Height between centers vertically
    const rows = 20;
    const cols = 30;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * hexWidth + (row % 2 === 1 ? hexWidth / 2 : 0);
        const y = row * hexHeight * 0.75;
        hexagons.push(
          <polygon
            key={`hex-${row}-${col}`}
            points={createHexagon(x, y, hexSize)}
            fill="none"
            stroke="#D97706"
            strokeWidth="2"
            style={{
              filter: 'drop-shadow(0 0 3px rgba(217, 119, 6, 0.4))',
            }}
          />
        );
      }
    }
    return hexagons;
  };

  return (
    <HeroContainer>
      {/* Honeycomb SVG Pattern */}
      <HoneycombSVG viewBox="0 0 2000 1000" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="hexGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#D97706" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {generateHoneycomb()}
      </HoneycombSVG>
      
      <HexagonOverlay />
      
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <HeroContent>
            <motion.div variants={itemVariants}>
              <Headline variant="h1">
                Catching bee parasites before they become a crisis.
              </Headline>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Subheadline variant="h5">
                AI-powered monitoring that detects Varroa mites weeks earlier than traditional methods.
              </Subheadline>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  fontSize: '1.2rem', 
                  color: beehiveTheme.neutral.lightGray,
                  textShadow: '0 2px 10px rgba(0,0,0,0.8)',
                  lineHeight: 1.8,
                }}
              >
                Continuous, automated hive monitoring using computer vision, acoustic analysis, and environmental sensors. 
                No bees sacrificed. No guesswork. Just early warnings when you need them.
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <CTAButton
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => scrollToSection('contact')}
                >
                  Get in Touch
                </CTAButton>
                <SecondaryButton
                  variant="outlined"
                  onClick={() => scrollToSection('problem')}
                >
                  Learn More
                </SecondaryButton>
              </Box>
            </motion.div>
          </HeroContent>
        </motion.div>
      </Container>
    </HeroContainer>
  );
}
